import { FaHome, FaFileAlt, FaEnvelope, FaMap, FaPaintBrush } from 'react-icons/fa';
import { BsMegaphone } from 'react-icons/bs';
import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../supabase';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import ProfileSettings from '../settings/ProfileSettings';
import NotificationsMenu from './NotificationsMenu';
import SearchResults from './SearchResults';
import WorkspaceSelector from './WorkspaceSelector';
import MarketingPlanModal from '../marketing/MarketingPlanModal';
import MarketingCalendarModal from '../planning/MarketingCalendarModal';
import Tooltip from '../ui/Tooltip';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useTheme } from '../../context/ThemeContext';
import { useGnomeContext } from '../../context/GnomeContext';
import { FiBarChart2, FiBell, FiCalendar, FiCheckSquare, FiChevronDown, FiFileText, FiHelpCircle, FiHome, FiImage, FiLayers, FiMap, FiMenu, FiMoon, FiPenTool, FiSearch, FiSettings, FiShare2, FiShield, FiSun, FiTarget, FiUser, FiUsers, FiX, FiZap , FiLayout} from 'react-icons/fi';
import { FaTrophy } from 'react-icons/fa';
import { BsPinAngle, BsPinAngleFill } from 'react-icons/bs';
import { HiOutlineSpeakerphone } from 'react-icons/hi';
import GnomesDropdown from './GnomesDropdown';
import PinnedTasksModal from '../dashboard/PinnedTasksModal';


const Header = () => {
  const navigate = useNavigate();
  const { activeWorkspace, userRole, hasFeature } = useWorkspace();
  const { theme, toggleTheme } = useTheme();
  const { gnomeNotifications, insights } = useGnomeContext();
  const teamUnreadCount = (gnomeNotifications?.length || 0) + (insights?.length || 0);
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(7);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMarketingPlan, setShowMarketingPlan] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [highlightEventId, setHighlightEventId] = useState(null);
  const [isTasksModalOpen, setIsTasksModalOpen] = useState(false);
  const [uncompletedTasksCount, setUncompletedTasksCount] = useState(0);

  useEffect(() => {
    const handleOpenCalendar = (e) => {
      if (e.detail?.eventId) {
        setHighlightEventId(e.detail.eventId);
      } else {
        setHighlightEventId(null);
      }
      setShowCalendar(true);
    };
    window.addEventListener('open-calendar-modal', handleOpenCalendar);
    return () => window.removeEventListener('open-calendar-modal', handleOpenCalendar);
  }, []);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Predefined Navigation Items
  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: FiHome },
    { name: 'Campaigns & Analytics', path: '/campaigns', icon: BsMegaphone },
    { name: 'Audience', path: '/audience', icon: FiUsers },
    { name: 'Content Library', path: '/engagement?tab=library', icon: FiImage },
    { name: 'Social Media', path: '/engagement', icon: FiShare2 },
    { name: 'Email Automation', path: '/email-automation', icon: FaEnvelope },
    { name: 'Settings', path: '/settings', icon: FiSettings },
    { name: 'Help Center', path: '/help', icon: FiHelpCircle },
    { name: 'Branding', path: '/branding', icon: FaPaintBrush },
  ];

  const userMenuRef = useRef(null);
  const notificationRef = useRef(null);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Toggle Search with Cmd+K or Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }

      // Close Search with Escape
      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
        setShowSearchResults(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }

      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
          setIsNotificationsOpen(false);
      }

      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Fetch current user and admin role
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

    };

    fetchUser();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch unread count
  useEffect(() => {
    if (activeWorkspace) {
      fetchUnreadCount();

      // Realtime subscription for badge
      const subscription = supabase
        .channel(`header_notifications:${activeWorkspace.id}`)
        .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `workspace_id=eq.${activeWorkspace.id}`
        }, () => {
            fetchUnreadCount();
        })
        .subscribe();

      return () => {
          supabase.removeChannel(subscription);
      };
    }
  }, [activeWorkspace]);

  // Presence
  useEffect(() => {
    if (!activeWorkspace || !user) return;

    const channel = supabase.channel(`workspace_presence:${activeWorkspace.id}`, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const users = Object.values(state).map(presences => presences[0]);
        setOnlineUsers(users);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: user.id,
            email: user?.email,
            full_name: user?.user_metadata?.full_name,
            avatar_url: user?.user_metadata?.avatar_url,
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeWorkspace, user]);

  const fetchUnreadCount = async () => {
      try {
          const { count, error } = await supabase
            .from('notifications')
            .select('*', { count: 'exact', head: true })
            .eq('workspace_id', activeWorkspace.id)
            .eq('is_read', false)
            .not('link', 'ilike', '/gnomes/%'); // Exclude gnome chat/insight notifications

          if (!error) {
              setUnreadCount(count || 0);
          }
      } catch (e) {
          console.error("Error fetching unread count", e);
      }
  };

  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `(${unreadCount}) SEVEN HATS®`;
    } else {
      document.title = 'SEVEN HATS®';
    }
  }, [unreadCount]);

  // Search Logic
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    // Filter Navigation Items Immediately
    const filteredNav = navItems.filter(item =>
        item.name.toLowerCase().includes(query.toLowerCase())
    );

    searchTimeoutRef.current = setTimeout(async () => {
      if (!activeWorkspace) {
        setSearchResults({
            nav: filteredNav,
            campaigns: [],
            creatives: []
        });
        setIsSearching(false);
        return;
      }

      try {
        // Search Campaigns
        const { data: campaigns } = await supabase
          .from('campaigns')
          .select('id, name, status, platforms')
          .eq('workspace_id', activeWorkspace.id)
          .ilike('name', `%${query}%`)
          .limit(5);

        // Search Creatives
        const { data: creatives } = await supabase
          .from('ad_creatives')
          .select('id, name, type, image_url')
          .eq('workspace_id', activeWorkspace.id)
          .ilike('name', `%${query}%`)
          .limit(5);

        setSearchResults({
          nav: filteredNav,
          campaigns: campaigns || [],
          creatives: creatives || []
        });
      } catch (error) {
        console.error('Search error:', error);
        // Fallback to showing just nav items if DB search fails
        setSearchResults({
            nav: filteredNav,
            campaigns: [],
            creatives: []
        });
      } finally {
        setIsSearching(false);
      }
    }, 500); // 500ms debounce
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setShowSearchResults(false);
  };

  // Calculate trial days left
  useEffect(() => {
    if (user?.created_at) {
      const created = new Date(user.created_at);
      const now = new Date();
      const diffTime = Math.abs(now - created);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const daysLeft = 7 - diffDays;
      setTrialDaysLeft(daysLeft > 0 ? daysLeft : 0);
    }
  }, [user]);

  // Determine display name
  const displayName = user?.user_metadata?.full_name || user?.email || 'Guest';
  const avatarUrl = user?.user_metadata?.avatar_url;
  const roleDisplay = userRole === 'super_admin' ? 'Super Admin' : 'User';

  return (
    <header className="bg-white dark:bg-charcoal-900 border-b border-charcoal-100 dark:border-charcoal-800 px-6 py-4 transition-colors z-[100] relative print:hidden">
      <div className="flex items-center justify-between">
        {/* Left: Logo & Nav */}
        <div className="flex items-center space-x-2 md:space-x-8">
          {/* Mobile App Logo */}
          <div className="lg:hidden">
              <Link to="/dashboard" className="flex items-center gap-2" aria-label="Home">
                 <img src="/sevenhats_logo_compact.webp" alt="Seven Hats Logo" width="32" height="32" className="w-8 h-8 object-contain rounded-lg" />
              </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
              <NavLink
                  to="/dashboard"
                  className={({ isActive }) => `group flex items-center gap-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white rounded px-1 ${isActive ? 'text-charcoal-900 dark:text-white' : 'text-charcoal-500 hover:text-charcoal-900 dark:text-charcoal-400 dark:hover:text-white'}`}
              >
                  Dashboard
              </NavLink>

              {/* Strategy Dropdown */}
              <div className="relative group">
                  <button className="flex items-center gap-1 text-sm font-medium text-charcoal-500 hover:text-charcoal-900 dark:text-charcoal-400 dark:hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white rounded px-1 py-2">
                      Strategy <FiChevronDown className="text-xs" />
                  </button>
                  <div className="absolute left-0 mt-0 w-48 bg-white dark:bg-charcoal-900 rounded-xl shadow-xl border border-charcoal-100 dark:border-charcoal-800 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden py-1">
                      <NavLink
                          to="/strategy"
                          className={({ isActive }) => `block px-4 py-2 text-sm transition-colors ${isActive ? 'bg-charcoal-50 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                      >
                          Strategy Hub
                      </NavLink>
                      {(userRole === 'super_admin' || hasFeature('flywheel_planner')) && (
                          <NavLink
                              to="/admin?tab=flywheel&sidebar=collapsed"
                              className={() => `block px-4 py-2 text-sm transition-colors ${new URLSearchParams(location.search).get('tab') === 'flywheel' ? 'bg-charcoal-50 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                          >
                              Flywheel
                          </NavLink>
                      )}
                  </div>
              </div>

              <NavLink
                  to="/technology"
                  className={({ isActive }) => `group flex items-center gap-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white rounded px-1 ${isActive ? 'text-charcoal-900 dark:text-white' : 'text-charcoal-500 hover:text-charcoal-900 dark:text-charcoal-400 dark:hover:text-white'}`}
              >
                  MarTech
              </NavLink>

              {/* Admin / Team Dropdown */}
              <div className="relative group">
                  <button className="flex items-center gap-1 text-sm font-medium text-charcoal-500 hover:text-charcoal-900 dark:text-charcoal-400 dark:hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white rounded px-1 py-2">
                      Admin <FiChevronDown className="text-xs" />
                  </button>
                  <div className="absolute left-0 mt-0 w-48 bg-white dark:bg-charcoal-900 rounded-xl shadow-xl border border-charcoal-100 dark:border-charcoal-800 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden py-1">
                      {(userRole === 'super_admin' || hasFeature('gnome_chat')) && (
                          <NavLink
                              to="/team-meeting"
                              className={({ isActive }) => `flex items-center justify-between px-4 py-2 text-sm transition-colors ${isActive ? 'bg-charcoal-50 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                          >
                              Team
                              {teamUnreadCount > 0 && (
                                  <span className="flex items-center justify-center min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                                      {teamUnreadCount > 9 ? '9+' : teamUnreadCount}
                                  </span>
                              )}
                          </NavLink>
                      )}
                      <NavLink
                          to="/billing"
                          className={({ isActive }) => `block px-4 py-2 text-sm transition-colors ${isActive ? 'bg-charcoal-50 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                      >
                          Billing
                      </NavLink>
                      {(userRole === 'super_admin' || hasFeature('marketing_tools')) && (
                          <NavLink
                              to="/admin?tab=marketing&sidebar=collapsed"
                              state={{ resetMarketing: true }}
                              className={() => `block px-4 py-2 text-sm transition-colors ${new URLSearchParams(location.search).get('tab') === 'marketing' ? 'bg-charcoal-50 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                          >
                              Marketing
                          </NavLink>
                      )}
                  </div>
              </div>
          </nav>


          {/* Search Button (Mobile/Tablet) or Icon (Desktop) */}
          <div className="relative" ref={searchContainerRef}>
             <Tooltip text="Search (⌘K)" position="bottom">
               <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className="p-2 rounded-lg hover:bg-charcoal-100 dark:hover:bg-charcoal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white"
                  aria-label="Search (Cmd+K)"
               >
                  <SafeIcon icon={FiSearch} className="text-charcoal-600 dark:text-charcoal-400 text-lg" />
               </button>
             </Tooltip>

             {isSearchOpen && (
                <div className="absolute left-0 mt-2 w-full min-w-[320px] max-w-lg bg-white dark:bg-charcoal-900 rounded-xl shadow-xl border border-charcoal-100 dark:border-charcoal-800 z-50 p-2 animate-fade-in">
                    <div className="relative">
                        <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal-400" />
                        <input
                        autoFocus
                        type="text"
                        placeholder="Search..."
                        aria-label="Search"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={() => {
                            if (searchQuery.trim()) setShowSearchResults(true);
                        }}
                        className="w-full pl-10 pr-10 py-2 bg-charcoal-50 dark:bg-charcoal-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-charcoal-900 dark:focus:ring-charcoal-700 text-charcoal-900 dark:text-white text-sm"
                        />
                        {searchQuery && (
                        <button
                            onClick={clearSearch}
                            aria-label="Clear search"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-charcoal-400 hover:text-charcoal-600 dark:hover:text-charcoal-200"
                        >
                            <SafeIcon icon={FiX} />
                        </button>
                        )}
                    </div>
                    {showSearchResults && (
                    <SearchResults
                        results={searchResults}
                        isLoading={isSearching}
                        onClose={() => setShowSearchResults(false)}
                        query={searchQuery}
                    />
                    )}
                </div>
             )}
          </div>
        </div>
        
        <div className="hidden md:flex items-center -space-x-2 mr-4">
             {onlineUsers.filter(ou => ou.user_id !== user?.id).map(ou => (
                 <Tooltip key={ou.user_id} text={ou.user_id === user?.id ? `${ou.full_name || ou.email} (You)` : (ou.full_name || ou.email)} position="bottom">
                     <div className="w-8 h-8 rounded-full border-2 border-white dark:border-charcoal-900 bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center overflow-hidden z-10 relative hover:z-20">
                       {ou.avatar_url ? (
                           <img src={ou.avatar_url} alt={ou.full_name || ou.email} width="32" height="32" loading="lazy" className="w-full h-full object-cover" />
                       ) : (
                           <span className="text-white text-xs font-bold">
                             {(ou.full_name || ou.email || 'U').charAt(0).toUpperCase()}
                           </span>
                       )}
                     </div>
                 </Tooltip>
             ))}
        </div>

        <div className="flex items-center space-x-2 md:space-x-2 border-none">
          {/* Desktop Only Icons */}
          {userRole === 'super_admin' && (
              <button
                  onClick={() => location.pathname === '/admin' ? navigate('/dashboard') : navigate('/admin')}
                  className="hidden md:flex px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full border transition-colors items-center gap-2 bg-white dark:bg-charcoal-800 text-charcoal-500 dark:text-charcoal-400 border-charcoal-200 dark:border-charcoal-700 hover:border-charcoal-500 hover:text-charcoal-500"
              >
                  <SafeIcon icon={FiLayout} /> {location.pathname === '/admin' ? 'USER VIEW' : 'ADMIN VIEW'}
              </button>
          )}

          <div className="hidden md:flex items-center space-x-4">
              <Tooltip text="Garden Seeds Usage" position="bottom">
                <div className="flex items-center gap-2 text-sm font-medium text-charcoal-600 dark:text-charcoal-400">
                    <span>🌱 {activeWorkspace?.garden_seeds || 0} / 500 seeds</span>
                </div>
              </Tooltip>
          </div>

          {/* Quick Score */}
          <div className="relative">
            <Tooltip text="Quick Score" position="bottom">
              <Link
                  to="/quick-score"
                  className="relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-charcoal-100 dark:hover:bg-charcoal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white"
                  aria-label="Quick Score"
              >
                  <span className="text-lg">⚡</span>
              </Link>
            </Tooltip>
          </div>

          {/* Pinned Tasks */}
          <div className="relative">
            <Tooltip text="Pinned Tasks & Gnome Tasks" position="bottom">
              <button
                  onClick={() => setIsTasksModalOpen(true)}
                  className="relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-charcoal-100 dark:hover:bg-charcoal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white"
                  aria-label="Pinned Tasks & Gnome Tasks"
              >
                  <SafeIcon icon={uncompletedTasksCount > 0 ? BsPinAngleFill : BsPinAngle} className={uncompletedTasksCount > 0 ? "text-red-500 text-lg" : "text-charcoal-400 text-lg"} />
              </button>
            </Tooltip>

            <PinnedTasksModal
                isOpen={isTasksModalOpen}
                onClose={() => setIsTasksModalOpen(false)}
                workspaceId={activeWorkspace?.id}
                onTasksCountChange={setUncompletedTasksCount}
            />
          </div>

          {userRole === 'super_admin' && (
            <div className="relative">
              <Tooltip text="Community Inbox" position="bottom">
                <Link
                    to="/community-inbox"
                    className="relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-charcoal-100 dark:hover:bg-charcoal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white"
                    aria-label="Community Inbox"
                >
                    <SafeIcon icon={HiOutlineSpeakerphone} className="text-charcoal-600 dark:text-charcoal-400 text-lg" />
                </Link>
              </Tooltip>
            </div>
          )}

          {/* Marketing Calendar */}
          <div className="relative">
            <Tooltip text="Marketing Calendar" position="bottom">
              <button
                  onClick={() => setShowCalendar(true)}
                  className="relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-charcoal-100 dark:hover:bg-charcoal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white"
                  aria-label="Marketing Calendar"
              >
                  <SafeIcon icon={FiCalendar} className="text-charcoal-600 dark:text-charcoal-400 text-lg" />
              </button>
            </Tooltip>
          </div>

          {/* Theme Toggle */}
          <div className="relative flex items-center mr-2">
            <Tooltip text={theme === 'dark' ? "Light Mode" : "Dark Mode"} position="bottom">
              <button
                onClick={toggleTheme}
                className="relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-charcoal-100 dark:hover:bg-charcoal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white"
                aria-label={theme === 'dark' ? "Switch to light mode" : "Switch to dark mode"}
              >
                <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} className="text-charcoal-600 dark:text-charcoal-400 text-lg" />
              </button>
            </Tooltip>
          </div>

          {/* Notifications */}
          <div className="relative flex items-center" ref={notificationRef}>
            <Tooltip text="Notifications" position="bottom">
              <button
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  className={`relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-charcoal-100 dark:hover:bg-charcoal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white ${isNotificationsOpen ? 'bg-charcoal-100 dark:bg-charcoal-800' : ''}`}
                  aria-label="Notifications"
              >
                  <SafeIcon icon={FiBell} className="text-charcoal-600 dark:text-charcoal-400 text-lg" />
                  {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full">
                          {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                  )}
              </button>
            </Tooltip>

            <NotificationsMenu
                activeWorkspace={activeWorkspace}
                isOpen={isNotificationsOpen}
                onClose={() => setIsNotificationsOpen(false)}
            />
          </div>
          
          {/* User Profile */}
          <div className="relative hidden md:block" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-3 hover:bg-charcoal-50 dark:hover:bg-charcoal-800 p-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white"
              aria-label="User menu"
            >
              <div className="w-8 h-8 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-center overflow-hidden">
                {avatarUrl ? (
                    <img src={avatarUrl} alt="Profile" width="32" height="32" loading="lazy" className="w-full h-full object-cover" />
                ) : (
                    <SafeIcon icon={userRole === 'super_admin' ? FiShield : FiUser} className="text-white text-sm" />
                )}
              </div>
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-charcoal-900 rounded-xl shadow-xl border border-charcoal-100 dark:border-charcoal-800 z-50 overflow-hidden animate-fade-in py-2">
                <Link to="/settings" onClick={() => setIsUserMenuOpen(false)} className="block px-4 py-2 text-sm text-charcoal-700 dark:text-charcoal-300 hover:bg-charcoal-50 dark:hover:bg-charcoal-800">
                    Account Settings
                </Link>
                <Link to="/settings/branding" onClick={() => setIsUserMenuOpen(false)} className="block px-4 py-2 text-sm text-charcoal-700 dark:text-charcoal-300 hover:bg-charcoal-50 dark:hover:bg-charcoal-800">
                    PDF Branding
                </Link>
                {userRole === 'super_admin' && (
                  <Link to="/client-meeting" onClick={() => setIsUserMenuOpen(false)} className="block px-4 py-2 text-sm text-charcoal-700 dark:text-charcoal-300 hover:bg-charcoal-50 dark:hover:bg-charcoal-800">
                      Client Meeting
                  </Link>
                )}
                <Link to="/help" onClick={() => setIsUserMenuOpen(false)} className="block px-4 py-2 text-sm text-charcoal-700 dark:text-charcoal-300 hover:bg-charcoal-50 dark:hover:bg-charcoal-800">
                    Help & Support
                </Link>
                <div className="border-t border-charcoal-100 dark:border-charcoal-800 my-1"></div>
                <button onClick={() => { setIsUserMenuOpen(false); supabase.auth.signOut(); }} className="w-full text-left block px-4 py-2 text-sm text-red-600 hover:bg-charcoal-50 dark:hover:bg-charcoal-800">
                    Log Out
                </button>
              </div>
            )}
          </div>

          {/* Hamburger Menu (Mobile/Tablet) */}
          <div className="lg:hidden relative">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-charcoal-100 dark:hover:bg-charcoal-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal-900 dark:focus-visible:ring-white"
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                  <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="text-charcoal-600 dark:text-charcoal-400 text-lg" />
              </button>

              {isMobileMenuOpen && (
                  <div className="fixed right-4 top-[80px] w-72 bg-white dark:bg-charcoal-900 rounded-xl shadow-2xl border border-charcoal-100 dark:border-charcoal-800 p-4 z-[9999] animate-fade-in max-h-[calc(100vh-100px)] overflow-y-auto">

                      {/* Mobile Nav Links */}
                      <div className="space-y-4 mb-4">
                          <div className="space-y-1">
                              <NavLink
                                    to="/dashboard"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-charcoal-100 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                              >
                                  Dashboard
                              </NavLink>
                              <NavLink
                                    to="/technology"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-charcoal-100 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                              >
                                  MarTech
                              </NavLink>
                          </div>

                          <div className="space-y-1">
                              <div className="px-3 py-1 text-xs font-semibold text-charcoal-400 uppercase tracking-wider">Strategy</div>
                              <NavLink
                                    to="/strategy"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-charcoal-100 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                              >
                                  Strategy Hub
                              </NavLink>
                              {(userRole === 'super_admin' || hasFeature('flywheel_planner')) && (
                                  <NavLink
                                      to="/admin?tab=flywheel&sidebar=collapsed"
                                      onClick={() => setIsMobileMenuOpen(false)}
                                      className={() => `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${new URLSearchParams(location.search).get('tab') === 'flywheel' ? 'bg-charcoal-100 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                                  >
                                      Flywheel
                                  </NavLink>
                              )}
                          </div>

                          <div className="space-y-1">
                              <div className="px-3 py-1 text-xs font-semibold text-charcoal-400 uppercase tracking-wider">Admin</div>
                              {(userRole === 'super_admin' || hasFeature('gnome_chat')) && (
                                  <NavLink
                                        to="/team-meeting"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-charcoal-100 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                                  >
                                      <div className="flex items-center gap-2">
                                          Team
                                          {teamUnreadCount > 0 && (
                                              <span className="flex items-center justify-center min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                                                  {teamUnreadCount > 9 ? '9+' : teamUnreadCount}
                                              </span>
                                          )}
                                      </div>
                                  </NavLink>
                              )}
                              <NavLink
                                    to="/billing"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? 'bg-charcoal-100 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                              >
                                  Billing
                              </NavLink>
                              {(userRole === 'super_admin' || hasFeature('marketing_tools')) && (
                                  <NavLink
                                      to="/admin?tab=marketing&sidebar=collapsed"
                                      state={{ resetMarketing: true }}
                                      onClick={() => setIsMobileMenuOpen(false)}
                                      className={() => `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${new URLSearchParams(location.search).get('tab') === 'marketing' ? 'bg-charcoal-100 dark:bg-charcoal-800 text-charcoal-900 dark:text-white' : 'text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800'}`}
                                  >
                                      Marketing
                                  </NavLink>
                              )}
                          </div>
                      </div>
                      {/* Mobile User/Settings */}
                      <div className="border-t border-charcoal-100 dark:border-charcoal-800 pt-4 space-y-2">
                          {userRole === 'super_admin' && (
                              <button onClick={() => { setIsMobileMenuOpen(false); location.pathname === '/admin' ? navigate('/dashboard') : navigate('/admin'); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800 rounded-lg">
                                  <SafeIcon icon={FiLayout} />
                                  {location.pathname === '/admin' ? 'User View' : 'Admin View'}
                              </button>
                          )}
                           <Link to="/settings" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2 text-sm text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800 rounded-lg">
                              <SafeIcon icon={FiSettings} />
                              Settings
                          </Link>
                          <button onClick={toggleTheme} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-charcoal-600 dark:text-charcoal-400 hover:bg-charcoal-50 dark:hover:bg-charcoal-800 rounded-lg">
                              <SafeIcon icon={theme === 'dark' ? FiSun : FiMoon} />
                              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                          </button>
                      </div>
                  </div>
              )}
          </div>

        </div>
      </div>

      <MarketingPlanModal isOpen={showMarketingPlan} onClose={() => setShowMarketingPlan(false)} />
      <MarketingCalendarModal isOpen={showCalendar} onClose={() => { setShowCalendar(false); setHighlightEventId(null); }} highlightEventId={highlightEventId} />
    </header>
  );
};

export default Header;
